// =====================================================
// PRICING RULES TAB
// Stage 2: Participants & Pricing
// Tab component for managing pricing rules in event detail view
// =====================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, ArrowUp, ArrowDown, Copy, Trash2, Power, PowerOff, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePermissions } from '@/hooks/usePermissions';
import { detectRuleConflicts } from '@/Services/pricingEngine.service';
import { usePricingLookups } from '../hooks/usePricingLookups';
import { useParticipantMutations } from '../hooks/useEventParticipants';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePricingRules, usePricingRuleMutations, usePricingRuleStatistics } from '../hooks/usePricingRules';
import {
  getPricingRuleSummary,
  getRuleStatusBadgeVariant,
  getRuleStatusLabel,
  isConditionBroken,
} from '@/models/pricingRule';
import { PricingRuleFormDialog } from './PricingRuleFormDialog';
import { RuleSimulator } from './RuleSimulator';
import type { PricingRule } from '@/types/pricing.types';

interface PricingRulesTabProps {
  eventId: string;
  basePrice: number;
}

export function PricingRulesTab({ eventId, basePrice }: PricingRulesTabProps) {
  const { rules, isLoading } = usePricingRules(eventId);
  const { lookups } = usePricingLookups();
  const mutations = usePricingRuleMutations(eventId);
  const { statistics } = usePricingRuleStatistics(eventId);
  const { can, canAny } = usePermissions();
  const canManage = canAny('event_pricing.edit', 'event_pricing.delete', 'event_pricing.create');
  const { recalculateAllPrices, isRecalculating } = useParticipantMutations(eventId);

  // Rule changes don't touch prices already calculated → offer recalculation
  const [pricesStale, setPricesStale] = useState(false);
  const signature = useMemo(
    () => JSON.stringify(rules.map((r) => [r.id, r.priority, r.is_active, r.conditions, r.action_type, r.action_value])),
    [rules],
  );
  const firstSignature = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (firstSignature.current === null) firstSignature.current = signature;
    else if (signature !== firstSignature.current) setPricesStale(true);
  }, [signature, isLoading]);

  const usage = useMemo(
    () => new Map((statistics as { rule_id: string; count: number }[]).map((s) => [s.rule_id, s.count])),
    [statistics],
  );
  const conflicts = useMemo(() => detectRuleConflicts(rules), [rules]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  // Handlers
  const handleCreate = () => {
    setEditingRule(null);
    setIsFormOpen(true);
  };

  const handleEdit = (rule: PricingRule) => {
    setEditingRule(rule);
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingRuleId) return;
    await mutations.deletePricingRule(deletingRuleId);
    setDeletingRuleId(null);
  };

  const handleToggleStatus = async (rule: PricingRule) => {
    await mutations.togglePricingRuleStatus(rule.id, !rule.is_active);
  };

  const handleMoveUp = async (ruleId: string) => {
    await mutations.movePricingRuleUp(ruleId);
  };

  const handleMoveDown = async (ruleId: string) => {
    await mutations.movePricingRuleDown(ruleId);
  };

  const handleDuplicate = async (ruleId: string) => {
    await mutations.duplicatePricingRule(ruleId);
  };

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Cargando reglas de precios">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Reglas de precios</h3>
          <p className="text-sm text-muted-foreground">
            Define quién paga qué precio. Se revisan de arriba hacia abajo y gana la primera que aplique.
          </p>
        </div>
        <Button onClick={handleCreate} disabled={!can('event_pricing.create')}>
          <Plus className="mr-2 size-4" />
          Nueva regla
        </Button>
      </div>

      {pricesStale && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>Las reglas cambiaron. Los precios ya calculados no se actualizan automáticamente (los pagados se mantienen).</span>
            <Button
              size="sm"
              variant="outline"
              disabled={isRecalculating}
              onClick={() => {
                recalculateAllPrices();
                firstSignature.current = signature;
                setPricesStale(false);
              }}
            >
              {isRecalculating ? <Loader2 className="mr-2 size-4 animate-spin" /> : <RefreshCw className="mr-2 size-4" />}
              Recalcular precios
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {conflicts.length > 0 && (
        <Alert variant={conflicts.some((c) => c.severity === 'high') ? 'destructive' : 'default'}>
          <AlertDescription>
            <p className="mb-1 flex items-center gap-2 font-medium">
              <AlertTriangle className="size-4" /> Reglas que requieren atención
            </p>
            <ul className="list-inside list-disc space-y-0.5 text-sm">
              {conflicts.slice(0, 5).map((c, i) => (
                <li key={i}>
                  «{c.rule1.name}» y «{c.rule2.name}»: {c.description}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-muted-foreground">
              Usa las flechas para cambiar el orden o desactiva / elimina la regla que sobra.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-12">
            <p className="text-center text-muted-foreground">
              No hay reglas de precios: todos pagan el precio base del evento.
              <br />
              Crea una regla para dar descuentos o precios especiales según grado, cargo, fecha de inscripción, etc.
            </p>
            <Button onClick={handleCreate} className="mt-4" disabled={!can('event_pricing.create')}>
              <Plus className="mr-2 size-4" />
              Crear primera regla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.map((rule, index) => (
            <Card key={rule.id} className={!rule.is_active ? 'opacity-60' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono" title="Orden de evaluación">
                        #{index + 1}
                      </Badge>
                      <CardTitle className="text-base">{rule.name}</CardTitle>
                      <Badge variant={getRuleStatusBadgeVariant(rule.is_active)}>
                        {getRuleStatusLabel(rule.is_active)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {usage.get(rule.id) ?? 0} participante(s)
                      </span>
                    </div>
                    {rule.description && (
                      <CardDescription className="text-sm">{rule.description}</CardDescription>
                    )}
                  </div>

                  {/* Actions (fieldset disables every button without the permission) */}
                  <fieldset disabled={!canManage} className="flex items-center gap-1 disabled:opacity-50">
                    {/* Move Up */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveUp(rule.id)}
                      disabled={index === 0}
                      title="Subir (se revisa antes)"
                    >
                      <ArrowUp className="size-4" />
                    </Button>

                    {/* Move Down */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMoveDown(rule.id)}
                      disabled={index === rules.length - 1}
                      title="Bajar (se revisa después)"
                    >
                      <ArrowDown className="size-4" />
                    </Button>

                    {/* Toggle Status */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleStatus(rule)}
                      title={rule.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {rule.is_active ? (
                        <PowerOff className="size-4" />
                      ) : (
                        <Power className="size-4" />
                      )}
                    </Button>

                    {/* Duplicate */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDuplicate(rule.id)}
                      title="Duplicar"
                    >
                      <Copy className="size-4" />
                    </Button>

                    {/* Edit */}
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(rule)}>
                      Editar
                    </Button>

                    {/* Delete */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingRuleId(rule.id)}
                      title="Eliminar"
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </fieldset>
                </div>
              </CardHeader>

              <CardContent>
                <p className="text-sm text-muted-foreground">{getPricingRuleSummary(rule, lookups)}</p>
                {rule.conditions.some((c) => isConditionBroken(c, lookups)) && (
                  <p className="mt-2 flex items-center gap-1 text-sm text-destructive">
                    <AlertTriangle className="size-4 shrink-0" />
                    Tiene criterios que no coinciden con ningún dato y nunca se cumplirán. Edítala y vuelve a elegir
                    las opciones.
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {rules.length > 0 && <RuleSimulator eventId={eventId} basePrice={basePrice} rulesSignature={signature} />}

      {/* Help Text */}
      <Card className="border-dashed">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            <strong>Cómo se elige la regla:</strong> se revisan en el orden de la lista (#1 primero) y se aplica solo
            la primera que coincida con el participante. Si ninguna coincide, paga el precio base.
          </p>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <PricingRuleFormDialog
        eventId={eventId}
        basePrice={basePrice}
        rule={editingRule}
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingRuleId} onOpenChange={() => setDeletingRuleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar regla de precios?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Los precios ya calculados no cambian hasta que uses
              «Recalcular precios».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
